import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';
import { dayToDayAccounts } from '../utils/accounts';
import { ensureSubject, ensureCategory } from '../utils/taxonomy';
import SearchableSelect from './SearchableSelect';
import Select from './Select';
import '../styles/forms.css';
import '../styles/EditRecurrence.css';

const frequencyOptions = [
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'YEARLY', label: 'Yearly' }
];

const recurrenceType = (t) => {
    if (t.type === 'Expense' || t.type === 'Income' || t.type === 'Transfer') {
        return t.type;
    }
    if (t.to_account_id != null && t.to_account_id !== t.account_id) {
        return 'Transfer';
    } else if (t.amount < 0) {
        return 'Expense';
    } else {
        return 'Income';
    }
};

const EditRecurrence = ({ recurrence, onCancel, onSuccess }) => {
    const [formData, setFormData] = useState({
        frequency: 'MONTHLY',
        interval: 1,
        start_date: new Date().toISOString().substring(0, 10),
        end_date: '',
        next_run_date: '',
        is_reconciled: false,
        notes: '',
        // Transaction template
        account_id: null,
        to_account_id: null,
        amount: 0,
        to_amount: 0,
        subject_id: null,
        subject_name: '',
        category_id: null,
        category_name: '',
        type: 'Expense',
        ...recurrence,
        amount: recurrence ? Math.abs(recurrence.amount) : 0,
        type: recurrence ? recurrenceType(recurrence) : 'Expense'
    });

    const [showAmountTo, setShowAmountTo] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [categories, setCategories] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [accountsRes, subjectsRes, categoriesRes] = await Promise.all([
                    api.get('/accounts'),
                    api.get('/subjects'),
                    api.get('/categories')
                ]);

                const filteredAccounts = accountsRes.data.filter(a => a.is_open === true);
                const sortedAccounts = filteredAccounts.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );

                setAccounts(sortedAccounts);
                setSubjects(subjectsRes.data);
                const sortedCategories = categoriesRes.data
                    .map(c => ({ ...c, label: c.full_name || c.name }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                setCategories(sortedCategories);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    // Reveal the differential "To Amount" field only when a stored transfer
    // moved a different amount into the destination account, mirroring
    // CreateTransaction's edit-mode behaviour.
    useEffect(() => {
        if (recurrence && recurrenceType(recurrence) === 'Transfer') {
            const toAmount = recurrence.to_amount;
            setShowAmountTo(
                toAmount != null &&
                Math.abs(Number(toAmount)) !== Math.abs(Number(recurrence.amount))
            );
        }
    }, [recurrence]);

    useEffect(() => {
        if (formData.type === 'Transfer' && categories.length > 0) {
            const transferCategory = categories.find(cat => cat.name === 'Transfer');
            if (transferCategory && formData.category_id !== transferCategory.id) {
                setFormData(prev => ({
                    ...prev,
                    category_id: transferCategory.id,
                    category_name: transferCategory.name,
                }));
            }
        }
    }, [formData.type, formData.category_id, categories]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAmountChange = (e) => {
        const { name, value } = e.target;
        const numericValue = value === '' ? 0 : parseFloat(value);

        let newValue = numericValue;
        if (formData.type !== 'Income') {
            newValue = -Math.abs(numericValue);
        }
        setFormData(prev => ({ ...prev, [name]: newValue }));
    };

    const handleAmountToChange = (e) => {
        const { name, value } = e.target;
        const numericValue = value === '' ? 0 : parseFloat(value);
        setFormData(prev => ({ ...prev, [name]: Math.abs(numericValue) }));
    };

    const handleSelectType = (type) => {
        setFormData(prev => ({
            ...prev,
            type: type ?? 'Expense',
        }));
    };

    const handleSelectAccount = (option) => {
        setFormData(prev => ({
            ...prev,
            account_id: option?.value || null,
            account_name: option?.label || ''
        }));
    };

    const handleSelectToAccount = (option) => {
        setFormData(prev => ({
            ...prev,
            to_account_id: option?.value || null,
            to_account_name: option?.label || ''
        }));
    };

    const handleSelectSubject = async (option) => {
        if (!option) {
            setFormData(prev => ({
                ...prev,
                subject_id: null,
                subject_name: '',
            }));
            return;
        }

        if (option.isNew) {
            try {
                const ensured = await ensureSubject(option.label);
                setSubjects(prev => (prev.some(s => s.id === ensured.value)
                    ? prev
                    : [...prev, { id: ensured.value, name: ensured.label }]));
                option = { value: ensured.value, label: ensured.label };
            } catch (err) {
                console.error('Error creating subject:', err);
                return;
            }
        }

        const selectedSubject = subjects.find(subject => subject.id === option.value);

        // Automatically select the default category if it exists
        const defaultCategory = selectedSubject?.default_category_id
            ? categories.find(category => category.id === selectedSubject.default_category_id)
            : null;

        setFormData(prev => ({
            ...prev,
            subject_id: option.value,
            subject_name: option.label,
            category_id: defaultCategory ? defaultCategory.id : prev.category_id,
            category_name: defaultCategory ? defaultCategory.name : prev.category_name,
        }));
    };

    const handleSelectCategory = async (option) => {
        if (!option) {
            setFormData(prev => ({
                ...prev,
                category_id: null,
                category_name: '',
            }));
            return;
        }

        if (option.isNew) {
            try {
                const ensured = await ensureCategory(option.label);
                setCategories(prev => (prev.some(c => c.id === ensured.value)
                    ? prev
                    : [...prev, { id: ensured.value, name: ensured.label, label: ensured.label }]));
                option = { value: ensured.value, label: ensured.label };
            } catch (err) {
                console.error('Error creating category:', err);
                return;
            }
        }
        setFormData(prev => ({
            ...prev,
            category_id: option?.value || null,
            category_name: option?.label || ''
        }));
    };

    // Expense/Income only offer day-to-day accounts; Transfers may fund or
    // withdraw from investment accounts, so those keep the full open list.
    const accountDropdownOptions = useMemo(() => (
        formData.type === 'Transfer'
            ? accounts.map(account => ({ value: account.id, label: account.name }))
            : dayToDayAccounts(accounts).map(account => ({ value: account.id, label: account.name }))
    ), [accounts, formData.type]);

    const validateForm = () => {
        if (!formData.account_id) {
            setError('Please select an account.');
            return false;
        }

        if (formData.type === 'Transfer') {
            if (!formData.to_account_id) {
                setError('Please select a destination account for transfers.');
                return false;
            }
            if (formData.to_account_id === formData.account_id) {
                setError('Source and destination accounts cannot be the same.');
                return false;
            }
        }

        // Check if the account is of type "cash" and the amount is zero
        const selectedAccount = accounts.find(account => account.id === formData.account_id);
        if (selectedAccount?.type === 'Cash' && Number(formData.amount) === 0) {
            setError('Amount cannot be zero for cash accounts.');
            return false;
        }

        setError('');
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        try {
            const dataToSubmit = {
                frequency: formData.frequency,
                interval: parseInt(formData.interval, 10) || 1,
                start_date: formData.start_date,
                end_date: formData.end_date || null,
                is_reconciled: formData.is_reconciled,
                notes: formData.notes || '',
                type: formData.type,
                account_id: formData.account_id,
                // Backend stores the sign (Expense/Transfer negative) from the
                // magnitude; the form keeps the input always positive.
                amount: formData.type === 'Income'
                    ? Math.abs(formData.amount)
                    : -Math.abs(formData.amount),
            };

            if (formData.type === 'Transfer') {
                dataToSubmit.to_account_id = formData.to_account_id;
                // to_amount is optional: default to the source magnitude.
                dataToSubmit.to_amount = showAmountTo
                    ? Math.abs(formData.to_amount)
                    : Math.abs(formData.amount);
            } else {
                dataToSubmit.subject_id = formData.subject_id;
                dataToSubmit.category_id = formData.category_id;
            }

            if (formData.id) {
                // Only edit mode sends the explicit next occurrence override;
                // the backend derives it for new recurrences.
                dataToSubmit.next_run_date = formData.next_run_date;
                await api.patch(`/recurrences/${formData.id}`, dataToSubmit);
            } else {
                await api.post('/recurrences', dataToSubmit);
            }
            onSuccess();
        } catch (error) {
            setError('Failed to save recurrence: ' + (error.response?.data?.error || error.message));
        }
    };

    const subjectOptions = subjects.map(s => ({ value: s.id, label: s.name }));
    const categoryOptions = categories.map(c => ({ value: c.id, label: c.label }));

    return (
        <form onSubmit={handleSubmit} className="form edit-recurrence-form">
            {error && <div className="error">{error}</div>}

            <div className="section-header">Schedule</div>

            <div className="form-group-row">
                <div className="form-group">
                    <label>Frequency
                        <Select
                            options={frequencyOptions}
                            onSelect={(option) => handleChange({
                                target: { name: 'frequency', value: option.value }
                            })}
                            value={formData.frequency}
                        />
                    </label>
                </div>
                <div className="form-group">
                    <label>Interval
                        <input
                            type="number"
                            name="interval"
                            min="1"
                            value={formData.interval}
                            onChange={handleChange}
                            required
                        />
                    </label>
                </div>
            </div>

            <div className="form-group-row">
                <div className="form-group">
                    <label>Start Date
                        <input
                            type="date"
                            name="start_date"
                            value={formData.start_date}
                            onChange={handleChange}
                            required
                        />
                    </label>
                </div>
                <div className="form-group">
                    <label>End Date (optional)
                        <input
                            type="date"
                            name="end_date"
                            value={formData.end_date}
                            onChange={handleChange}
                        />
                    </label>
                </div>
            </div>

            {formData.id && (
                <div className="form-group-row">
                    <div className="form-group">
                        <label>Next Occurrence
                            <input
                                type="date"
                                name="next_run_date"
                                value={formData.next_run_date}
                                onChange={handleChange}
                            />
                        </label>
                    </div>
                </div>
            )}

            <div className="section-header">Transaction Template</div>

            <div className="form-group">
                <div className="radio-group">
                    {['Expense', 'Income', 'Transfer'].map((type) => (
                        <label key={type} className="radio-option">
                            <span className="radio-label">{type}</span>
                            <input
                                type="radio"
                                name="recurrenceType"
                                value={type}
                                checked={formData.type === type}
                                onChange={(e) => handleSelectType(e.target.value)}
                            />
                            <span className="radio-custom"></span>
                        </label>
                    ))}
                </div>
            </div>

            <div className="form-group-row">
                <div className="form-group">
                    <label>Amount
                        <input
                            type="number"
                            name="amount"
                            value={Math.abs(formData.amount)}
                            onChange={handleAmountChange}
                            required
                        />
                    </label>
                </div>
                {formData.type === 'Transfer' && showAmountTo && (
                    <div className="form-group">
                        <label>Amount To
                            <input
                                type="number"
                                name="to_amount"
                                value={formData.to_amount === 0 ? '' : Math.abs(formData.to_amount)}
                                onChange={handleAmountToChange}
                                required
                            />
                        </label>
                    </div>
                )}
            </div>

            {formData.type === 'Transfer' && (
                <div className="form-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={showAmountTo}
                            onChange={(e) => setShowAmountTo(e.target.checked)}
                        />
                        <span className="checkbox-custom" />
                        <span className="checkbox-text">Set different amount for destination account</span>
                    </label>
                </div>
            )}

            <div className="form-group-row">
                <div className="form-group">
                    <label>{formData.type === 'Transfer' ? 'From Account' : 'Account'}
                        <SearchableSelect
                            options={accountDropdownOptions}
                            onSelect={handleSelectAccount}
                            value={formData.account_id}
                        />
                    </label>
                </div>
                {formData.type === 'Transfer' && (
                    <div className="form-group">
                        <label>To Account
                            <SearchableSelect
                                options={accounts.map(a => ({ value: a.id, label: a.name }))}
                                onSelect={handleSelectToAccount}
                                value={formData.to_account_id}
                            />
                        </label>
                    </div>
                )}
            </div>

            {formData.type !== 'Transfer' && (
                <div className="form-group-row">
                    <div className="form-group">
                        <label>{formData.type === 'Expense' ? 'Subject/Payee' : 'From'}
                            <SearchableSelect
                                options={subjectOptions}
                                onSelect={handleSelectSubject}
                                value={formData.subject_id}
                                allowCreate={true}
                            />
                        </label>
                    </div>
                    <div className="form-group">
                        <label>Category
                            <SearchableSelect
                                options={categoryOptions}
                                onSelect={handleSelectCategory}
                                value={formData.category_id}
                                allowCreate={true}
                            />
                        </label>
                    </div>
                </div>
            )}

            {formData.type === 'Transfer' && (
                <div className="form-group">
                    <label>Category
                        <SearchableSelect
                            options={categoryOptions}
                            onSelect={handleSelectCategory}
                            value={formData.category_id}
                            allowCreate={true}
                        />
                    </label>
                </div>
            )}

            <div className="form-group">
                <label>Notes
                    <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows="3"
                    />
                </label>
            </div>

            <div className="form-group">
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        name="is_reconciled"
                        checked={formData.is_reconciled}
                        onChange={handleChange}
                    />
                    <span className="checkbox-custom" />
                    <span className="checkbox-text">Reconciled</span>
                </label>
            </div>

            <div className="form-buttons">
                <button type="button" className="btn btn--secondary" onClick={onCancel}>
                    Cancel
                </button>
                <button type="submit" className="btn btn--primary">
                    {formData.id ? 'Save Changes' : 'Create Recurrence'}
                </button>
            </div>
        </form>
    );
};

export default EditRecurrence;